import React from 'react'
import { Form, Input, Spin, Space, Select, InputNumber, Flex, Checkbox } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { colorOptions } from '../../../../../utils/antd.helper'
import {
    getPatternWarningLevelColor,
    getPatternWarningLevelName,
    PatternWarningLevels,
} from '../../../../../constants/pattern-warning-levels'
import { GROUPING_CAP_TYPES } from '../../../../../constants/grouping-cap-types'
import PatternIcon from '../../../../../components/common/PatternIcon'
import ConceptualLabelSelect from '../../../../resources/conceptual-labels/select/ConceptualLabelSelect'
import CriteriaFields from '../../../../../components/common/criteria/CriteriaFields'
import TextEditor from '../../../../../components/common/text-editor/TextEditor'
import { ConceptualLabelScopes } from '../../../../../constants/conceptual-label-scopes'
import { PATTERN_NAME_LIMIT } from '../../../../../constants/input-limits'

const warningLevelOptions = Object.values(PatternWarningLevels).map(level => ({
    label: (
        <Space size={8}>
            <PatternIcon
                id={level}
                color={getPatternWarningLevelColor(level)}
                showId={false}
                type='warning'
                style={{}}
                suffix=''
            />{' '}
            {getPatternWarningLevelName(level)}
        </Space>
    ),
    value: level,
}))

const groupingCapOptions = Object.values(GROUPING_CAP_TYPES).map(groupingCapType => ({
    label: groupingCapType.label,
    value: groupingCapType.value,
}))

export const FormContent = ({
    semanticPattern,
    criteriaWithProjection,
    criteriaThatAreWaitingForRegenerate,
    setCriteriaFields,
    onRegenerateAndProject,
    setFocusedCriterion,
}) => {
    // Parse feedback for semantic patterns if it exists
    let hitFeedback = ''
    let missFeedback = ''
    let showHitReasoning = false
    let showMissReasoning = false

    if (semanticPattern?.feedback) {
        hitFeedback = semanticPattern.feedback ?? semanticPattern.hit_feedback
        missFeedback = semanticPattern.miss_feedback || ''
        showHitReasoning = semanticPattern.show_hit_reasoning || false
        showMissReasoning = semanticPattern.show_miss_reasoning || false
    }

    return (
        <>
            <Form.Item label='Name' name={`name-${semanticPattern.id}`} initialValue={semanticPattern.name} rules={[
                { max: PATTERN_NAME_LIMIT, message: `Pattern name cannot exceed ${PATTERN_NAME_LIMIT} characters` }
            ]}>
                <Input placeholder='Enter name for this pattern' maxLength={PATTERN_NAME_LIMIT} showCount/>
            </Form.Item>
            <Spin
                spinning={criteriaThatAreWaitingForRegenerate.includes(semanticPattern.id)}
                tip='Regenerating pattern...'
            >
                <Form.Item
                    label='Conceptual Label'
                    name={`cl-${semanticPattern.id}`}
                    initialValue={
                        semanticPattern.proposed_conceptual_labels ? undefined : semanticPattern.conceptual_labels
                    }
                    tooltip='Conceptual labels identify the key  principles to be demonstrated in student work. Select labels that connect specific work patterns to broader educational concepts the student demonstrates or needs to develop.'
                >
                    <ConceptualLabelSelect
                        proposedConceptualLabels={semanticPattern.proposed_conceptual_labels || []}
                        scope={ConceptualLabelScopes.ALL}
                    />
                </Form.Item>
            </Spin>
            <Form.Item
                label='Description'
                name={`description-${semanticPattern.id}`}
                initialValue={semanticPattern.description}
            >
                <TextArea rows={4} placeholder='Enter description for this pattern' />
            </Form.Item>
            <Spin
                spinning={criteriaThatAreWaitingForRegenerate.includes(semanticPattern.id)}
                tip='Regenerating feedback...'
            >
                <Form.Item
                    name={`hit-feedback-${semanticPattern.id}`}
                    initialValue={hitFeedback}
                    extra={
                        <Form.Item
                            name={`show-hit-reasoning-${semanticPattern.id}`}
                            valuePropName='checked'
                            initialValue={showHitReasoning}
                            noStyle
                        >
                            <Checkbox>Show Reasoning to Students on Hit</Checkbox>
                        </Form.Item>
                    }
                >
                    <TextEditor label='Hit Feedback' placeholder='Enter hit feedback for this pattern' />
                </Form.Item>

                <Form.Item
                    name={`miss-feedback-${semanticPattern.id}`}
                    initialValue={missFeedback}
                    extra={
                        <Form.Item
                            name={`show-miss-reasoning-${semanticPattern.id}`}
                            valuePropName='checked'
                            initialValue={showMissReasoning}
                            noStyle
                        >
                            <Checkbox>Show Reasoning to Students on Miss</Checkbox>
                        </Form.Item>
                    }
                >
                    <TextEditor label='Miss Feedback' placeholder='Enter miss feedback for this pattern' />
                </Form.Item>
            </Spin>
            <Spin
                spinning={criteriaThatAreWaitingForRegenerate.includes(semanticPattern.id)}
                tip='Regenerating criteria...'
            >
                <CriteriaFields
                    fields={criteriaWithProjection}
                    setFields={fields => setCriteriaFields(semanticPattern.id, fields)}
                    regenerateReasoning={
                        semanticPattern.file_content ? () => onRegenerateAndProject(semanticPattern.id) : null
                    }
                    setFocusedCriterion={setFocusedCriterion}
                />
            </Spin>
            <Space className='d-flex-space-between'>
                <Form.Item label='Color' name={`color-${semanticPattern.id}`} initialValue={semanticPattern.color_id}>
                    <Select
                        options={colorOptions}
                        popupMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                        showSearch
                        optionFilterProp='label'
                    />
                </Form.Item>
                <Form.Item
                    label='Warning Level'
                    name={`warning-level-${semanticPattern.id}`}
                    initialValue={semanticPattern.warning_level}
                    rules={[{ required: true, message: 'Please, select warning level' }]}
                >
                    <Select
                        options={warningLevelOptions}
                        popupMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                        showSearch
                        optionFilterProp='label'
                    />
                </Form.Item>
            </Space>
            <Flex justify='space-between'>
                <Form.Item
                    label='Grade'
                    name={`grade-${semanticPattern.id}`}
                    initialValue={semanticPattern.grade ?? 0}
                    rules={[{ type: 'number', message: 'Please, input a number' }]}
                >
                    <InputNumber />
                </Form.Item>
                <Form.Item
                    label='Grouping Cap'
                    name={`grouping-cap-${semanticPattern.id}`}
                    initialValue={semanticPattern.grouping_cap ?? GROUPING_CAP_TYPES.ONE.value}
                    rules={[{ required: true, message: 'Please, select grouping cap' }]}
                >
                    <Select
                        options={groupingCapOptions}
                        popupMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                        showSearch
                        optionFilterProp='label'
                    />
                </Form.Item>
            </Flex>
        </>
    )
}
